import { ObjectSchema, array, object, string, number, boolean } from "yup";
import { staticTexts } from "../texts";
import {
  requiredIfPredicateIsTruthy,
  testBeloeb,
  testStringNumberNotNegative,
} from "../../../util/validation";
import { KlientkontoerklaeringFormValues } from "../../../interfaces/klientkontoerklaering";
import { getAfstemning } from "../../../util/klientkontoerklaering";

const isNonZeroValue = (value: string) => {
  const numValue = parseFloat(value?.replace(",", ".") || "0");
  return numValue !== 0;
};

export const KLIENTKONTOERKLAERING_FORM_VALIDATION_SCHEMA: ObjectSchema<KlientkontoerklaeringFormValues> =
  object({
    advokatens_section_1_1_samleklientbankkonti: array()
      .required("Påkrævet")
      .of(
        object({
          pengeinstitution: string().required("Pengeinstitut er påkrævet"),
          regKontoNr: string().required("Reg- og kontonr. er påkrævet"),
          opretDato: string(),
          cvrKonto: string(),
        }),
      ),
    advokatens_section_1_2_bogføringssystem: string().required(
      "Bogføringssystem er påkrævet",
    ),
    advokatens_section_1_3_aabningsdag: string().required(
      "Åbningsdag er påkrævet",
    ),
    advokatens_section_1_4_dispensation_ja_nej: string().required(
      "Vælg Ja eller Nej for dispensation",
    ),
    advokatens_section_1_5_dispensationDato: string().when(
      "advokatens_section_1_4_dispensation_ja_nej",
      {
        is: "Ja",
        then: (schema) => schema.required("Påkrævet"),
      },
    ),
    advokatens_section_1_5_dispensationSagsnr: string().when(
      "advokatens_section_1_4_dispensation_ja_nej",
      {
        is: "Ja",
        then: (schema) => schema.required("Påkrævet"),
      },
    ),
    advokatens_section_1_5_dispensationPeriodeDatoFra: string().when(
      "advokatens_section_1_4_dispensation_ja_nej",
      {
        is: "Ja",
        then: (schema) => schema.required("Påkrævet"),
      },
    ),
    advokatens_section_1_5_dispensationPeriodeDatoTil: string().when(
      "advokatens_section_1_4_dispensation_ja_nej",
      {
        is: "Ja",
        then: (schema) => schema.required("Påkrævet"),
      },
    ),
    advokatens_section_1_6_bogføringAntalDage: number()
      .typeError("Skal være et tal")
      .min(1, "Antal dage skal være større end 0")
      .max(7, "Antal dage må ikke være større end 7")
      .when("advokatens_section_1_4_dispensation_ja_nej", {
        is: "Ja",
        then: (schema) => schema.required("Påkrævet"),
      }),
    advokatens_section_2_1_samletKlienttilsvar_input: string()
      .required("Samlet klienttilsvar er påkrævet")
      .test(
        "2_1_samletKlienttilsvar",
        "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
        testBeloeb,
      ),
    advokatens_section_2_1_samletKlienttilsvar_beloeb: number(),
    advokatens_section_2_2_indestaaende_input: string()
      .required("Indestående på konto er påkrævet")
      .test(
        "2_2_indestaaende",
        "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
        testBeloeb,
      ),
    advokatens_section_2_3_ingen_regulering: boolean().when(
      [
        "advokatens_section_2_2_indestaaende_beloeb",
        "advokatens_section_2_1_samletKlienttilsvar_beloeb",
      ],
      {
        is: (klienttilsvar: number, indestaaende: number) => {
          const diff = (klienttilsvar || 0) - (indestaaende || 0);
          return diff === 0;
        },
        then: (schema) =>
          schema.required("Påkrævet").oneOf([true], "Feltet skal afkrydses"),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
    advokatens_section_2_2_indestaaende_beloeb: number(),
    advokatens_section_2_3_forMeget_forLidt: string().when(
      "advokatens_section_2_3_forMegetellerLidtIndestaaende_input",
      {
        is: (value: any) => value > 0 || value < 0,
        then: (schema) => schema.required("Påkrævet"),
      },
    ),
    advokatens_section_2_3_forMegetellerLidtIndestaaende_input: string()
      .test(
        "2_3_forMegetellerLidtIndestaaende",
        "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
        testBeloeb,
      )
      .test(
        "2_3_d_positivt",
        "Beløb (absolutværdi) skal være større end 0, anvend for meget/for lidt indestående for at vælge positivt eller negativt beløb",
        testStringNumberNotNegative,
      )
      .min(1, "Beløb skal være større end 0"),
    advokatens_section_2_3_forMegetellerLidtIndestaaende_beloeb: number(),
    advokatens_section_2_3_haevet_indsat: string().test(
      "2_3_haevet_indsat_dato",
      "Påkrævet",
      function (value) {
        const inputValue =
          this.parent
            .advokatens_section_2_3_forMegetellerLidtIndestaaende_input;
        return requiredIfPredicateIsTruthy(value, isNonZeroValue(inputValue));
      },
    ),
    advokatens_section_2_3_haevet_indsat_dato: string().test(
      "2_3_haevet_indsat_dato",
      "Påkrævet",
      function (value) {
        const inputValue =
          this.parent
            .advokatens_section_2_3_forMegetellerLidtIndestaaende_input;
        return requiredIfPredicateIsTruthy(value, isNonZeroValue(inputValue));
      },
    ),
    advokatens_section_3_1_pengeinstitut_indestaende_input: string()
      .required("Påkrævet")
      .test(
        "3_1_pengeinstitut_indestaende",
        "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
        testBeloeb,
      )
      .min(0, "Beløb skal være større end 0"),
    advokatens_section_3_1_pengeinstitut_indestaende_beloeb: number(),
    advokatens_section_3_3: object({
      advokatens_section_3_3_a_input: string()
        .test(
          "3_3_a_positivt",
          "Beløb (absolutværdi) skal være større end 0, anvend plus/minus-knappen for at vælge positivt eller negativt beløb",
          testStringNumberNotNegative,
        )
        .test(
          "3_3_a",
          "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
          testBeloeb,
        ),
      advokatens_section_3_3_a_plus_minus: string(),
      advokatens_section_3_3_b_input: string()
        .test(
          "3_3_b_positivt",
          "Beløb (absolutværdi) skal være større end 0, anvend plus/minus-knappen for at vælge positivt eller negativt beløb",
          testStringNumberNotNegative,
        )
        .test(
          "3_3_b",
          "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
          testBeloeb,
        ),
      advokatens_section_3_3_b_plus_minus: string(),
      advokatens_section_3_3_c_input: string()
        .test(
          "3_3_c_positivt",
          "Beløb (absolutværdi) skal være større end 0, anvend plus/minus-knappen for at vælge positivt eller negativt beløb",
          testStringNumberNotNegative,
        )
        .test(
          "3_3_c",
          "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
          testBeloeb,
        ),
      advokatens_section_3_3_c_plus_minus: string(),
      advokatens_section_3_3_d_input: string()
        .test(
          "3_3_d_positivt",
          "Beløb (absolutværdi) skal være større end 0, anvend plus/minus-knappen for at vælge positivt eller negativt beløb",
          testStringNumberNotNegative,
        )
        .test(
          "3_3_d",
          "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
          testBeloeb,
        ),
      advokatens_section_3_3_d_plus_minus: string(),
      advokatens_section_3_3_e_input: string()
        .test(
          "3_3_e_positivt",
          "Beløb (absolutværdi) skal være større end 0, anvend plus/minus-knappen for at vælge positivt eller negativt beløb",
          testStringNumberNotNegative,
        )
        .test(
          "3_3_e",
          "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
          testBeloeb,
        ),
      advokatens_section_3_3_e_plus_minus: string(),
      advokatens_section_3_3_e_beskrivelse: string(),
    }).test("3_3_afstemning", "Afstemning skal være i balance", function () {
      const afstemning = getAfstemning(this.parent);
      return afstemning === "0,00";
    }),

    revisors_0_intro_overholder_revisorloven: string().required("Påkrævet"),
    revisors_0_intro_aftalebrev_indgået: string().required("Påkrævet"),
    revisors_handling_1_question: string().required("Påkrævet"),
    revisors_handling_1_question_alt_2_samleklientbankkonti: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_1_question ===
          staticTexts.revisors_handling_1.revisors_handling_1_question
            .revisors_handling_1_question_alt_2.id
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_1_question_alt_3_samleklientbankkonti: string().test(
      "Required if alt 3",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_1_question ===
          staticTexts.revisors_handling_1.revisors_handling_1_question
            .revisors_handling_1_question_alt_3.id
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_1_question_alt_4_beskrivelse: string().test(
      "Required if alt 4",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_1_question ===
          staticTexts.revisors_handling_1.revisors_handling_1_question
            .revisors_handling_1_question_alt_4.id
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_2_question: string().required("Påkrævet"),
    revisors_handling_2_alt_1_og_3_antal_bokonti_stikprover: number()
      .typeError("Skal være et tal")
      .min(1, "Mindst 1")
      .max(10, "Max 10")
      .test("Required if alt 1 or 3", "Påkrævet", function (value) {
        if (
          this.parent.revisors_handling_2_question ===
            staticTexts.revisors_handling_2.revisors_handling_2_question
              .revisors_handling_2_question_alt_1.id ||
          this.parent.revisors_handling_2_question ===
            staticTexts.revisors_handling_2.revisors_handling_2_question
              .revisors_handling_2_question_alt_3.id
        )
          return !!value;
        return true;
      }),
    revisors_handling_2_question_alt_3: number()
      .typeError("Skal være et tal")
      .min(0, "Mindst 0"),
    revisors_handling_2_alt_3_antal_bokonti_ikke_opprettet_i_kurators_bobestyrers_advokatselskabets_navn:
      number().typeError("Skal være et tal").min(0),
    revisors_handling_2_alt_3_antal_bokonti_ikke_opprettet_i_boets_navn:
      number().typeError("Skal være et tal").min(0),
    revisors_handling_2_alt_3_antal_bokonti_stikprover: number()
      .typeError("Skal være et tal")
      .min(0),
    revisors_handling_2_alt_3_antal_bokonti_ikke_separate: number()
      .typeError("Skal være et tal")
      .min(0),
    revisors_handling_2_alt_3_bemaerkning: string().test(
      "Required if alt 3",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_2_question ===
          staticTexts.revisors_handling_2.revisors_handling_2_question
            .revisors_handling_2_question_alt_3.id
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_2_alt_3_antal_bokonti_ikke_oprettet: number()
      .typeError("Skal være et tal")
      .min(0),
    revisors_handling_3_question: string().required("Påkrævet"),
    revisors_handling_3_alt_2_bogfoeringssystem: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_3_question ===
          staticTexts.revisors_handling_3.revisors_handling_3_question
            .revisors_handling_3_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_4_revisionsbesoeg_dato: string().required("Påkrævet"),
    revisors_handling_4_question: string().required("Påkrævet"),
    revisors_handling_4_alt_2_uddybning: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_4_question ===
          staticTexts.revisors_handling_4.revisors_handling_4_question
            .revisors_handling_4_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_5_question: string().required("Påkrævet"),
    revisors_handling_5_alt_2_beskriv_uoverenstemmelsen: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        return requiredIfPredicateIsTruthy(
          value,
          this.parent.revisors_handling_5_question ===
            staticTexts.revisors_handling_5.revisors_handling_5_question
              .revisors_handling_5_question_alt_2.value,
        );
      },
    ),
    revisors_handling_6_question: string().required("Påkrævet"),
    revisors_handling_6_alt_2_dato_reguleret: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_6_question ===
          staticTexts.revisors_handling_6.revisors_handling_6_question
            .revisors_handling_6_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_6_alt_2_forklaring: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_6_question ===
          staticTexts.revisors_handling_6.revisors_handling_6_question
            .revisors_handling_6_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_6_alt_3_uddybning: string().test(
      "Required if alt 3",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_6_question ===
          staticTexts.revisors_handling_6.revisors_handling_6_question
            .revisors_handling_6_question_alt_3.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_7_question: string().required("Påkrævet"),
    revisors_handling_7_alt_2_beskrivelse: string().test(
      "Required if alt 3",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_7_question ===
          staticTexts.revisors_handling_7.revisors_handling_7_question
            .revisors_handling_7_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_8_question: string().required("Påkrævet"),
    revisors_handling_8_alt_2_beskrivelse: string().test(
      "Required if alt 3",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_8_question ===
          staticTexts.revisors_handling_8.revisors_handling_8_question
            .revisors_handling_8_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_9_question: string().required("Påkrævet"),
    revisors_handling_9_alt_2_beskrivelse: string().test(
      "Required if alt 3",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_9_question ===
          staticTexts.revisors_handling_9.revisors_handling_9_question
            .revisors_handling_9_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_10_question: string().required("Påkrævet"),
    revisors_handling_10_alt_1_dispensationDato: string().test(
      "Required if alt 1",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_10_question ===
          staticTexts.revisors_handling_10.revisors_handling_10_question
            .revisors_handling_10_question_alt_1.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_10_alt_1_dispensationPeriodeDatoFra: string().test(
      "Required if alt 1",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_10_question ===
          staticTexts.revisors_handling_10.revisors_handling_10_question
            .revisors_handling_10_question_alt_1.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_10_alt_1_dispensationPeriodeDatoTil: string().test(
      "Required if alt 1",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_10_question ===
          staticTexts.revisors_handling_10.revisors_handling_10_question
            .revisors_handling_10_question_alt_1.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_10_alt_2_uddybning: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_10_question ===
          staticTexts.revisors_handling_10.revisors_handling_10_question
            .revisors_handling_10_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_11_periode_fra: string().required("Påkrævet"),
    revisors_handling_11_periode_til: string().required("Påkrævet"),
    revisors_handling_11_question: string().required("Påkrævet"),
    revisors_handling_11_alt_2_uddybning: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_11_question ===
          staticTexts.revisors_handling_11.revisors_handling_11_question
            .revisors_handling_11_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_12_table: array()
      .required("Påkrævet")
      .of(
        object({
          revisors_handling_12_table_columns_dato: string().test(
            "Required if first row",
            "Påkrævet",
            function (value) {
              // Is this the best way to find the first row?
              if (this.path.indexOf("[0]") > -1) {
                return !!value;
              }
              return true;
            },
          ),
          revisors_handling_12_table_column_samlet_tilsvar_input: string()
            .test(
              "3_3_d",
              "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
              testBeloeb,
            )
            .test("Required if first row", "Påkrævet", function (value) {
              if (this.path.indexOf("[0]") > -1) {
                return !!value;
              }
              return true;
            }),
          revisors_handling_12_table_column_reguleringsbeloeb_plus_minus:
            string(),
          revisors_handling_12_table_column_reguleringsbeloeb_input: string()
            .test(
              "3_3_d",
              "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
              testBeloeb,
            )
            .test(
              "3_3_d_positivt",
              "Beløb (absolutværdi) skal være større end 0, anvend plus/minus-knappen for at vælge positivt eller negativt beløb",
              testStringNumberNotNegative,
            )
            .test("Required if first row", "Påkrævet", function (value) {
              if (this.path.indexOf("[0]") > -1) {
                return !!value;
              }
              return true;
            }),
          revisors_handling_12_table_column_reguleringsdato: string(),
          revisors_handling_12_table_column_mellemvaerende_input: string().test(
            "3_3_d",
            "Beløb må kun angives med cifre, ét decimaltegn og max to decimaler.",
            testBeloeb,
          ),
        }),
      ),
    revisors_handling_12_question: string().required("Påkrævet"),
    revisors_handling_12_alt_2_uddybning: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_12_question ===
          staticTexts.revisors_handling_12.revisors_handling_12_question
            .revisors_handling_12_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_13_antal_transaktioner_udvalgt_som_stikproeve: number()
      .typeError("Skal være et tal")
      .required("Påkrævet")
      .min(0, "Mindst 0")
      .max(25, "Max 25"),
    revisors_handling_13_question: string().required("Påkrævet"),
    revisors_handling_13_question_alt_2_uddybning_table: array()
      .required("Påkrævet")
      .of(
        object({
          id: string(),
          revisors_handling_13_question_alt_2_uddybning_table_column_begrundelse:
            string().test(
              "Required if alt 2",
              "Påkrævet",
              function (value, _ctx) {
                if (
                  this.from &&
                  this.from.length > 1 &&
                  this.from[1].value.revisors_handling_13_question ===
                    staticTexts.revisors_handling_13
                      .revisors_handling_13_question
                      .revisors_handling_13_question_alt_2.value
                )
                  return !!value;
                return true;
              },
            ),
          revisors_handling_13_question_alt_2_uddybning_table_column_beloeb:
            string().test("Required if alt 2", "Påkrævet", function (value) {
              if (
                this.from &&
                this.from.length > 1 &&
                this.from[1].value.revisors_handling_13_question ===
                  staticTexts.revisors_handling_13.revisors_handling_13_question
                    .revisors_handling_13_question_alt_2.value
              )
                return !!value;
              return true;
            }),
          revisors_handling_13_question_alt_2_uddybning_table_column_bilagsnummer:
            string().test("Required if alt 2", "Påkrævet", function (value) {
              if (
                this.from &&
                this.from.length > 1 &&
                this.from[1].value.revisors_handling_13_question ===
                  staticTexts.revisors_handling_13.revisors_handling_13_question
                    .revisors_handling_13_question_alt_2.value
              )
                return !!value;
              return true;
            }),
          revisors_handling_13_question_alt_2_uddybning_table_column_dato:
            string().test("Required if alt 2", "Påkrævet", function (value) {
              if (
                this.from &&
                this.from.length > 1 &&
                this.from[1].value.revisors_handling_13_question ===
                  staticTexts.revisors_handling_13.revisors_handling_13_question
                    .revisors_handling_13_question_alt_2.value
              )
                return !!value;
              return true;
            }),
        }),
      ),
    revisors_handling_14_question: string().required("Påkrævet"),
    revisors_handling_14_alt_2_uddybning: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_14_question ===
          staticTexts.revisors_handling_14.revisors_handling_14_question
            .revisors_handling_14_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_15_question: string().required("Påkrævet"),
    revisors_handling_15_alt_2_uddybning: string().test(
      "Required if alt 2",
      "Påkrævet",
      function (value) {
        if (
          this.parent.revisors_handling_15_question ===
          staticTexts.revisors_handling_15.revisors_handling_15_question
            .revisors_handling_15_question_alt_2.value
        )
          return !!value;
        return true;
      },
    ),
    revisors_handling_16_question: string().required("Påkrævet"),
    revisors_handling_16_antal_kreditnotaer_udvalgt_som_stikproeve: number()
      .typeError("Skal være et tal")
      .required("Påkrævet")
      .min(0, "Mindst 0")
      .max(25, "Max 25"),
    revisors_handling_16_question_alt_2_uddybning_table: array()
      .required("Påkrævet")
      .of(
        object({
          id: string().required("Påkrævet"),
          revisors_handling_16_question_alt_2_uddybning_table_column_fakturanr:
            string().test("Required if alt 2", "Påkrævet", function (value) {
              if (
                this.from &&
                this.from.length > 1 &&
                this.from[1].value.revisors_handling_16_question ===
                  staticTexts.revisors_handling_16.revisors_handling_16_question
                    .revisors_handling_16_question_alt_2.value
              )
                return !!value;
              return true;
            }),
          revisors_handling_16_question_alt_2_uddybning_table_column_fakturadato:
            string().test("Required if alt 2", "Påkrævet", function (value) {
              if (
                this.from &&
                this.from.length > 1 &&
                this.from[1].value.revisors_handling_16_question ===
                  staticTexts.revisors_handling_16.revisors_handling_16_question
                    .revisors_handling_16_question_alt_2.value
              )
                return !!value;
              return true;
            }),
          revisors_handling_16_question_alt_2_uddybning_table_column_fakturabeloeb:
            string().test("Required if alt 2", "Påkrævet", function (value) {
              if (
                this.from &&
                this.from.length > 1 &&
                this.from[1].value.revisors_handling_16_question ===
                  staticTexts.revisors_handling_16.revisors_handling_16_question
                    .revisors_handling_16_question_alt_2.value
              )
                return !!value;
              return true;
            }),
          revisors_handling_16_question_alt_2_uddybning_table_column_modtager_navn_cvr:
            string().test("Required if alt 2", "Påkrævet", function (value) {
              if (
                this.from &&
                this.from.length > 1 &&
                this.from[1].value.revisors_handling_16_question ===
                  staticTexts.revisors_handling_16.revisors_handling_16_question
                    .revisors_handling_16_question_alt_2.value
              )
                return !!value;
              return true;
            }),
          revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_nr:
            string().test("Required if alt 2", "Påkrævet", function (value) {
              if (
                this.from &&
                this.from.length > 1 &&
                this.from[1].value.revisors_handling_16_question ===
                  staticTexts.revisors_handling_16.revisors_handling_16_question
                    .revisors_handling_16_question_alt_2.value
              )
                return !!value;
              return true;
            }),
          revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_dato:
            string(),
          revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_beloeb:
            string(),
          revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_begrundelse:
            string().test("Required if alt 2", "Påkrævet", function (value) {
              if (
                this.from &&
                this.from.length > 1 &&
                this.from[1].value.revisors_handling_16_question ===
                  staticTexts.revisors_handling_16.revisors_handling_16_question
                    .revisors_handling_16_question_alt_2.value
              )
                return !!value;
              return true;
            }),
          revisors_handling_16_question_alt_2_uddybning_table_column_ny_faktura_modtager_navn_cvr:
            string(),
          revisors_handling_16_question_alt_2_uddybning_table_column_ny_faktura_beloeb:
            string(),
        }),
      ),
    revisors_oevrige_oplysninger_kundeforhold_accepteret_dato:
      string().required("Påkrævet"),
    revisors_oevrige_oplysninger_arbejsdomraade_checkbox_revision_af_aarsregnskab:
      boolean(),
    revisors_oevrige_oplysninger_arbejsdomraade_checkbox_udvidet_gennemgang_af_aarsregnskab:
      boolean(),
    revisors_oevrige_oplysninger_arbejsdomraade_checkbox_review_af_aarsregnskab:
      boolean(),
    revisors_oevrige_oplysninger_arbejsdomraade_checkbox_assistance_med_regnskabsopslilling_af_aarsregnskab:
      boolean(),
    revisors_oevrige_oplysninger_arbejsdomraade_checkbox_bogfoering: boolean(),
    revisors_oevrige_oplysninger_revisors_navn: string().required("Påkrævet"),
    revisors_oevrige_oplysninger_adr_postnr_by: string().required("Påkrævet"),
    revisors_oevrige_oplysninger_adr_vej_husnr: string().required("Påkrævet"),
    revisors_oevrige_oplysninger_cvrnr: string().required("Påkrævet"),
    revisors_oevrige_oplysninger_firmanavn: string().required("Påkrævet"),
    revisors_oevrige_oplysninger_revisor_type: string().required("Påkrævet"),
  });
